import * as Actions from './Actions'
import * as OverpanelActions from './overpanels'
import { fetch, post, postJson } from '../../calculator/lib/xhr'
import {
  INSTRUMENTATION_TYPES,
  ActionTypes as types,
  CLIENT_MISSING_REQUIRED_NOTE_FIELD,
  FLOW_TYPES,
} from '../constants'
import * as travelRuleUtils from '../utils/travelRuleUtils'
import { routeTo } from '../utils/routeHelper'
import { getRecentActions } from '../../transfer/middlewares/action-history'
import {
  getAutoSelectedFundingOption,
  removePreferredFromSourcesInOption,
  addPreferredToSourceInOption,
  shouldForceAutoSelectedOption,
  getSingleFundingOption,
  isFundingOptionAvailable,
  getOptionById,
  getRelevantFundingSource,
  buildFundingSourceAccountType,
} from '../utils/funding-option-utils'
import { getFlowType } from '../utils/url-utils'
import FeaturesConfig from '../../../../config/features.json'
import { FundingOptionSelector, SendSuccess } from 'transfer/lazy-components'
import { generateNegBalFlowUrl } from '../../experiments/fiFlow'
import { isV2PhaseTwo } from '../../experiments/v2'
import { isIframe } from 'iframe-support'
import {
  cardinalDDCCallPromise,
  extract3DS2Contingency,
  track3ds2PostDDCAnalytics,
  collectBrowserContext,
} from '../utils/3ds-utils'
import { isEmpty, get } from 'lodash'
import { trackAction } from '../../lib/analytics/fpti'
import {
  MONEY_PAGE,
  INSTRUMENT_TYPES,
  CONTINGENCY,
  PAYMENT_TYPE,
} from '../../../../lib/constants'
import { rankingPreferencesHelper } from './sendHelpers'
import { batch } from 'react-redux' // eslint-disable-line no-restricted-imports
import { getTrafficSource } from '../utils/mgmUtils'
import { calculateAttributesToCollect } from '../utils/travelRuleUtils'
import { isHideShippingAddress } from '../../../../lib/address'
import { isEmail, isPhone } from '../../../../shared/validators'
import logger from 'loglevel'
import { handleProductDetails } from '../utils/local-storage'

function makeFundingOptionsCall(
  dispatch,
  state,
  {
    isExternal = false,
    conversionType = null,
    preselectedFundingOption = {},
    instrumentIds = [],
    handleFISelectorNext = null,
  } = {}
) {
  const {
    server: {
      env,
      psd2: { token: psd2Token } = {},
      groupFeatures: { shouldShowSelectFundingOptionsPage = false },
      isMgm,
      flowIntent,
    },
    note: { note: noteToPayee = '' },
    amount: { amount, currencyCode },
    recipient: {
      token,
      payerId = '',
      recipient,
      networkId = '',
      displayName = '',
      productId = '',
      productQuantity = '',
      accountId = '',
      thumbnailUrl = '',
      recipientId,
      recipientIdType,
      moneyPageOverPanel,
      pispRecipientType = '',
      pispRecipientId = '',
    },
    address: {
      selectedShippingAddress: { id: shippingAddressId },
    },
    transaction: {
      isPurchase,
      whoPaysFee,
      selectedFundingOption: { funding_sources },
      successfullyAddedFundingInstrumentIds,
      serviceReturnedType,
      shippingAddressCollectionPreference,
    },
    unilateral: {
      countryCode: unilateralCountry,
      name: recipientFullName,
    } = {},
    theme: { themeId },
    crossBorder: { toCountryCode, toCurrencyCode, fromCurrencyCode, fxQuoteId },
    external: {
      poolId,
      anonymousPayment,
      donationContext,
      recurring,
      donateToken,
      fromUL,
      sdkMeta,
      targetMeta,
      transaction_attribute,
    },
    donate: { donationAnonymousStatus },
  } = state

  const ignorePreferred = Boolean(successfullyAddedFundingInstrumentIds)
  // our server uses both of these, and also is checking for the string "true"
  const action = isPurchase ? 'buy' : 'send'
  const goods = isPurchase ? 'true' : 'false'

  let paymentCardId

  // grab paymentCardId if user is changing currency conversion type
  if (conversionType) {
    const paymentCard =
      funding_sources &&
      funding_sources.find(
        source => source.instrument_type === INSTRUMENT_TYPES.PAYMENT_CARD
      )
    paymentCardId = paymentCard && paymentCard.id
  }

  // For mandatory FI, generate additional params for funding_option call
  const preferences = rankingPreferencesHelper({
    conversionType,
    instrumentIds,
    preselectedFundingOption,
  })

  const browserData = collectBrowserContext()
  const browserContext = {
    screenHeight: browserData.browser_screen_height,
    screenWidth: browserData.browser_screen_width,
    colorDepth: browserData.browser_color_depth,
    timezoneOffsetMinutes: browserData.browser_timezone_offset_minutes,
    language: browserData.browser_language,
    javaEnabled: browserData.browser_java_enabled,
  }

  const shouldSendShippingAddress = !isHideShippingAddress(
    shippingAddressCollectionPreference
  )

  // query params being sent to the server
  let payload = {
    env,
    payerId,
    networkId,
    recipient,
    displayName,
    thumbnailUrl,
    amount,
    ...(shouldSendShippingAddress && { shippingAddressId }),
    currencyCode,
    goods,
    action,
    serviceReturnedType,
    themeId,
    noteToPayee,
    flowType: getFlowType(),
    paymentCardId,
    conversionType,
    moneyPageExperience: moneyPageOverPanel,
    recipientCountry: unilateralCountry,
    successfullyAddedFundingInstrumentIds,
    whoPaysFee,
    // mixin additional fields based on env
    ...(env === 'xb' && { toCountryCode }),
    ...(env === 'ppme' && { recipientId, recipientIdType }), // ppme
    ...(env === 'pools' && {
      poolId,
      anonymousPayment,
    }), // pools
    ...(recipientFullName && { recipientFullName }), // kyc
    ...(env === 'donate' && {
      donationContext,
      recurring,
      donateToken,
      fromUL,
      sdkMeta,
      targetMeta,
      transaction_attribute,
      donationAnonymousStatus,
    }),
    ...(env === 'pisp' && psd2Token && { psd2Token }),
    ...(env === 'pisp' &&
      pispRecipientType &&
      pispRecipientId && { pispRecipientType, pispRecipientId }), // this is required for funding options calls
    isMgm,
    token,
    browserContext,
    flowIntent,
    fxQuoteId,
    ...(env === FLOW_TYPES.PPSHOPS && {
      items: handleProductDetails(
        accountId,
        networkId,
        productId,
        productQuantity
      ),
    }),
  }

  if (preferences) {
    payload.preferences = preferences
  }

  const url = '/myaccount/transfer/fi'

  post(url, payload)
    .then(response => {
      const { data = {} } = response
      let responseData = data
      // Add "conversionType" to the funding option call response data. In the case of funding a cross currency transaction
      // using card, we need to know the conversion type to show the conversion message "Converst currency with PAyPal | card"
      responseData = { ...responseData, conversionType }

      // As we have debounced pricing call in cross currency scenario, sometimes
      // amount in store mismatch with the amount sent to planning, hence here
      // we use amount sent to planning to update amount in store so that we have
      // consistent amount on Review and Success screen.
      if (
        (toCurrencyCode !== fromCurrencyCode &&
          responseData.destinationAmount &&
          responseData.destinationAmount.value &&
          responseData.destinationAmount.currency) ||
        env === FLOW_TYPES.PPSHOPS
      ) {
        dispatch(Actions.changeAmount(responseData.destinationAmount.value))
        dispatch(
          Actions.changeCurrency(responseData.destinationAmount.currency)
        )
      }

      dispatch(
        receivedFundingOptions(
          responseData,
          shouldShowSelectFundingOptionsPage,
          ignorePreferred
        )
      )

      if (handleFISelectorNext) {
        handleFISelectorNext()
      }
    })
    .catch(err => {
      if (err.responseJSON && err.responseJSON.code === 'poolsCurrencyError') {
        dispatch(Actions.triggerExternalHook('noCurrencyBalanceError', err))
      }

      switch (err.code) {
        case 'negativeBalance':
          const mainWindow = isIframe() ? window.top : window
          mainWindow.location = generateNegBalFlowUrl({
            flowType: getFlowType(),
            amount: amount,
            currencyCode: currencyCode,
            payerId,
            networkId,
            recipient: recipient,
            displayName,
            thumbnailUrl,
            isMgm,
            mgmTrafficSource: getTrafficSource(),
            flowIntent,
            env,
            token,
          })
          return
        case 'unilateral':
          return dispatch(showUnilateral(state.user.country))

        case 'noFundingSource':
        case 'errorExpiredCreditCard':
          const unavailableFI = Boolean(
            successfullyAddedFundingInstrumentIds.length
          )
          return dispatch(Actions.noFundingSource(unavailableFI))

        case 'authRequired':
          return // do nothing bc we are redirecting the user to auth flow in xhr.js

        case 'cipRequired':
          /**
           * For AU users who haven't completed CIP and want to Send Money F&F,
           * we show an overpanel that can redirect them to CIP flow.
           */
          return dispatch(
            OverpanelActions.toggleConfirmIdentityOverpanel({ show: true })
          )
        case CLIENT_MISSING_REQUIRED_NOTE_FIELD:
          return dispatch(Actions.mandatoryNoteMissing(true))

        default:
          return dispatch(Actions.showError(err))
      }
    })
}

export function getAnalyticsMeta(type, payload) {
  return { analytics: { type, payload } }
}

export function fetchFundingOptions({
  conversionType = null,
  preselectedFundingOption = {},
  instrumentIds = [],
  handleFISelectorNext = null,
} = {}) {
  // preload the chunk file
  FundingOptionSelector.preload()

  return (dispatch, getState) => {
    makeFundingOptionsCall(dispatch, getState(), {
      conversionType,
      instrumentIds,
      preselectedFundingOption,
      handleFISelectorNext,
    })
    dispatch({
      type: types.TRANSACTION.FETCH_FUNDING_OPTIONS,
    })
  }
}

export function receivedFundingOptions(
  fundingData,
  shouldShowSelectFundingOptionsPage,
  ignorePreferred
) {
  let {
    fundingOptions = [],
    disallowedSources = [],
    complianceInfo = {},
    isPaypalAccount = false,
    additionalDisclosureActions = [],
    xbActualRecipientCountry = '',
    xbChosenCountry = '',
    xbCountryMismatch = false,
    isCrowdFundingCampaign = false,
    riskHoldOverpanelVariant = '',
    isPreferredSourceDisallowed,
    conversionType,
    showFISelector = true,
    selectedFundingOptionId: fundingOptionIdFromSession = '',
    showStateMandatedWarning = false,
    paymentTypeReviewMessage = '',
    items = [],
    shippingDetails = {},
    shippingType = '',
    purchaseConsentPreChecked = false,
    purchaseConsentChecked = false,
    purchaseConsentLabel = '',
    purchaseConsentRequired = false,
  } = fundingData

  const xbData = {
    xbActualRecipientCountry,
    xbChosenCountry,
    xbCountryMismatch,
  }

  // we are tracking this receipt of funding options below, but this also needs to be
  // tracked as per the analytics team
  const flowType = getFlowType()

  return (dispatch, getState) => {
    const {
      card: { addedCards },
      theme: { themeId = '' } = {},
      transaction: {
        isPurchase,
        fundingSourceIdsOfLastSelectedFundingOption: sourceIdsToMatch,
        successfullyAddedFundingInstrumentIds,
      },
      external: { isPortable } = {},
    } = getState()

    // try to get the preselected Funding Option
    let preselectedFundingOption = {}
    fundingOptions.forEach(option => {
      if (option && option.preselected_funding_option) {
        preselectedFundingOption = option
      }
    })

    // try to automatically select a funding option from payload data
    const [newestAddedCard] = addedCards
    let selectedFundingOption = getAutoSelectedFundingOption(
      fundingOptions,
      isPurchase,
      sourceIdsToMatch,
      newestAddedCard,
      ignorePreferred
    )

    // When showFISelector is False and there is a preselectedFundingOption,
    // we make the preselectedFundingOption and don't show the funding option selector
    if (!showFISelector && !isEmpty(preselectedFundingOption)) {
      selectedFundingOption = preselectedFundingOption
    }

    // portable always gets a funding options (until we can add choice)
    // TODO: add full support for choice in portable
    if (isPortable && !selectedFundingOption) {
      selectedFundingOption = getSingleFundingOption(fundingOptions)
    }

    if (fundingOptionIdFromSession) {
      selectedFundingOption = getOptionById(
        fundingOptions,
        fundingOptionIdFromSession
      )
    }

    if (
      !selectedFundingOption &&
      shouldForceAutoSelectedOption(
        isPurchase,
        shouldShowSelectFundingOptionsPage
      )
    ) {
      // if not in the choice experiment, we need to make sure a funding option is selected since we won't be
      // forced to see the interstitial initially
      selectedFundingOption = getSingleFundingOption(fundingOptions)
    }

    // Handle DDC calls by first collecting, storing all DDCs, and then firing
    // one by one sequentially in DeviceDataCollection.js
    const ddcCalls = collect3ds2DDCCalls(fundingOptions)
    if (ddcCalls.length > 0) {
      dispatch(storeAllDDC(ddcCalls))
    }

    // Toggle a warning when a new Funding Instrument is added in Wallet
    // And that FI is not available for the transaction
    const newFundingOptionIsAvailable = isFundingOptionAvailable(
      fundingOptions,
      successfullyAddedFundingInstrumentIds
    )
    if (successfullyAddedFundingInstrumentIds && !newFundingOptionIsAvailable) {
      dispatch(toggleFIUnavailableWarning(true))
    }

    // create meta data for analytics
    let pageGroup = `main:consumer:p2p:${flowType}::confirm`
    let isRTR =
      complianceInfo &&
      complianceInfo.action === 'DISPLAY_TRANSACTION_DISCLOSURES'
    let flavor = isRTR ? 'rtr' : ''
    let pageName = `main:consumer:p2p:${flowType}::confirm:${flavor}`

    /**
     * we pass pp_flow analytics data when the user is via send money flow and
     * decides to send a gift
     * we do this by checking if their exist a themeId and is via send money route
     *
     * we also pass `iscvvshown` to analytics when the user sends money using a funding
     * option that has a 'CVV' contingency
     **/
    let analyticsMeta

    const { contingencies = [] } = selectedFundingOption || {} // we cannot assume that selectedFundingOption exists here
    const hasCVVContingency = contingencies.some(
      contingency => contingency.action === 'CVV_REQUIRED'
    )

    if (hasCVVContingency || (themeId && flowType === 'send')) {
      const data = {
        ...(themeId && flowType && { pp_flow: 'p2p_gift' }),
        ...(hasCVVContingency && { iscvvshown: true }),
      }
      analyticsMeta = Actions.getAnalyticsMeta(
        INSTRUMENTATION_TYPES.ACTION_WITH_DATA,
        { linkName: 'continue', pageGroup, pageName, data }
      )
    }

    dispatch({
      type: types.TRANSACTION.RECEIVED_FUNDING_OPTIONS,
      meta: analyticsMeta,
      data: {
        ...xbData,
        items,
        shippingDetails,
        selectedFundingOption,
        preselectedFundingOption,
        additionalDisclosureActions,
        fundingOptions,
        riskHoldOverpanelVariant,
        disallowedSources,
        complianceInfo,
        isPaypalAccount,
        isPreferredSourceDisallowed,
        isCrowdFundingCampaign,
        conversionType,
        showFISelector,
        showStateMandatedWarning,
        paymentTypeReviewMessage,
        shippingType,
        purchaseConsentPreChecked,
        purchaseConsentChecked,
        purchaseConsentLabel,
        purchaseConsentRequired,
      },
      receivedAt: Date.now(),
    })
  }
}

/**
 * This function collects all the DDC contingencies for 3DS2 from wallet
 * @param fundingOptions
 * @param dispatch
 */
export function collect3ds2DDCCalls(fundingOptions) {
  const ddcCalls = []
  fundingOptions.forEach(({ id = '', contingencies = [] }) => {
    contingencies.forEach(contingency => {
      const {
        action = '',
        three_d_secure_protocol: threeDSecureProtocol = '',
        jwt = '',
        three_ds_reference_id: threeDS2ReferenceId = '',
        device_data_collection_url: deviceDataCollectionUrl = '',
        device_data_collection_mode: deviceDataCollectionMode = '',
      } = contingency
      if (
        action === '3D_SECURE_DATA_COLLECTION_REQUIRED' &&
        threeDSecureProtocol === '2.0'
      ) {
        ddcCalls.push({
          jwt,
          deviceDataCollectionMode,
          deviceDataCollectionUrl,
          threeDSecureProtocol,
          threeDS2ReferenceId,
          fundingOptionId: id,
        })
      }
    })
  })
  return ddcCalls
}

export function endDDCCall() {
  return {
    type: types.TRANSACTION.END_DDC_CALL,
  }
}

export function startDDCCall(ddcInfo) {
  return {
    type: types.TRANSACTION.START_DDC_CALL,
    data: ddcInfo,
  }
}

export function storeAllDDC(ddcCalls) {
  return {
    type: types.TRANSACTION.STORE_ALL_DDC,
    data: { ddcCalls: ddcCalls },
  }
}

export function clearFundingOptions() {
  return {
    type: types.TRANSACTION.CLEAR_FUNDING_OPTIONS,
  }
}

export function setPurchaseConsentValue(purchaseConsentChecked) {
  return {
    type: types.TRANSACTION.PURCHASE_CONSENT_IS_CHECKED,
    data: { purchaseConsentChecked: purchaseConsentChecked },
  }
}

/**
 * Called when the user presses the "back" button on the browser for 3DS flow.
 */
export function refetchFundingOption3DS() {
  return {
    type: types.TRANSACTION.REFETCH_FUNDING_OPTIONS_3DS,
  }
}

/**
 * This function tries to resolve the DDC Contingency for a funding option
 * While the processing is happening the spinner screen is shown for atleast two seconds
 * and till we get a response from resolve-contingency API
 *
 * @param sendSubmit
 * @returns {Function}
 */

export function resolveDDC(
  sendSubmit,
  history = {},
  isGuest = false,
  browserContext = {}
) {
  return async (dispatch, getState) => {
    const {
      transaction: { selectedFundingOption = {}, refetchedFundingOptionFor3DS },
      server: { env = '' },
    } = getState()

    const { id: fundingOptionId } = selectedFundingOption

    const data = {
      fundingOptionId,
      browserContext,
      isGuest,
      env,
    }
    const url = `/myaccount/transfer/${
      isGuest ? 'guestLogin' : 'send'
    }/ddc-contingency`

    // The change here is trying to stop resolve DDC call that is running in the background when browser back is pressed.
    // When browser back is initiated refetchedFundingOptionFor3DS is set to true. So that the resolveDDC already initiated
    // can be cancelled. refetchedFundingOptionFor3DS is reset when TRANSACTION.RECEIVED_FUNDING_OPTIONS is triggered.
    if (refetchedFundingOptionFor3DS) return
    // start the spinner
    dispatch(OverpanelActions.toggle3DS2SpinnerOverpanel({ show: true }))
    const startTime = performance.now()

    // change the pglk as this action is trigerred by click from Send Payment Now
    trackAction('Send Payment Now')

    // Only call DDC if deviceDataCollectionMode is SYNCHRONOUS
    const ddcInfo = collect3ds2DDCCalls([selectedFundingOption])[0]
    const isSynchronous =
      get(ddcInfo, 'deviceDataCollectionMode', '') === 'SYNCHRONOUS'
    if (isSynchronous) {
      // Wait for DDC call promise to resolve before continuing
      await cardinalDDCCallPromise(dispatch, ddcInfo)
    }

    fetch(url, data)
      .then(response => {
        let endTime = performance.now()
        let timeDiff = (endTime - startTime) / 1000
        // check if the time taken for resolve contingency is greater than 2 seconds, stop the spinner,
        // if less than 2 seconds, show the spinner
        // The purpose of this code is to add a delay of minimum of 2 seconds
        while (timeDiff <= 2) {
          endTime = performance.now()
          timeDiff = (endTime - startTime) / 1000
        }
        // stop the spinner
        dispatch(OverpanelActions.toggle3DS2SpinnerOverpanel({ show: false }))

        track3ds2PostDDCAnalytics(isGuest, startTime, endTime)

        if (response && response.isDDCSuccess) {
          // show the challenge overpanel as there is another contingency in response of resolving the DDC Contingency
          if (response.threeDS2contingency) {
            dispatch({
              type: types.TRANSACTION.THREE_DS2_SECURE_AUTH_REQUIRED,
              data: response.threeDS2contingency,
            })
            // Adding setTimeout as multiple action dispatch causes the screen to freeze
            setTimeout(() => {
              sendSubmit()
              Actions.resetScrollPosition()
            }, 500)
          } else {
            // show the success screen as there is no challenge
            dispatch({ type: types.TRANSACTION.DDC_RESOLVED })
            sendSubmit()
            Actions.resetScrollPosition()
          }
        }
      })
      .catch(err => {
        dispatch(OverpanelActions.toggle3DS2SpinnerOverpanel({ show: false }))
        let endTime = performance.now()
        track3ds2PostDDCAnalytics(isGuest, startTime, endTime)
        setTimeout(() => {
          dispatch(Actions.showError(err))
        }, 500)
      })
  }
}

export function handleCVV(sendSubmit) {
  return (dispatch, getState) => {
    const {
      transaction: {
        selectedFundingOption: { id: fundingOptionId },
        cvv,
      },
      server: { env },
    } = getState()

    const sendData = {
      fundingOptionId,
      cvv,
      env,
    }

    dispatch({ type: types.TRANSACTION.REQUEST_CVV })

    const url = '/myaccount/transfer/send/cvv-contingency'
    post(url, sendData)
      .then(response => {
        dispatch({
          type: types.TRANSACTION.RESOLVE_CVV,
          data: response,
        })
        // Re-submit to proceed with transaction
        sendSubmit()
        Actions.resetScrollPosition()
      })
      .catch(err => {
        dispatch(Actions.showError(err))
      })
  }
}

export function handlePurchaseConsent(sendSubmit, isGuest = false) {
  return (dispatch, getState) => {
    const {
      transaction: {
        selectedFundingOption: { id: fundingOptionId },
        purchaseConsentChecked,
        purchaseConsentRequired,
      },
      server: { env },
    } = getState()

    const sendData = {
      fundingOptionId,
      purchaseConsentChecked,
      purchaseConsentRequired,
      env,
    }

    dispatch({ type: types.TRANSACTION.REQUEST_PURCHASE_CONSENT })

    const userFlowType = isGuest ? 'guestLogin' : 'send'
    const url = `/myaccount/transfer/${userFlowType}/purchase-consent-contingency`
    post(url, sendData)
      .then(response => {
        dispatch({ type: types.TRANSACTION.RESOLVE_PURCHASE_CONSENT })
        // Re-submit to proceed with transaction
        sendSubmit()
        Actions.resetScrollPosition()
      })
      .catch(err => {
        dispatch(Actions.showError(err))
      })
  }
}

export function handleThreeDS2Success(threeDS2Contingency) {
  return {
    type: types.TRANSACTION.RESOLVED_3DS2_AUTH_CONTINGENCY,
    data: threeDS2Contingency,
  }
}

export function handleThreeDS2Failure() {
  return { type: types.TRANSACTION.FAILED_3DS2_AUTH_CONTINGENCY }
}

export function handleThreeDS() {
  return { type: types.TRANSACTION.RESOLVE_3DS }
}

export function errorThreeDS(err) {
  return dispatch => {
    dispatch(OverpanelActions.toggle3DSecureOverpanel({ show: false }))
    dispatch(Actions.showError(err))
    dispatch(clearFundingOptions())
  }
}

export const setDualControlContext = ({ handleNext = null }) => {
  return dispatch => {
    dispatch({ type: types.TRANSACTION.SET_DUALCONTROL_CONTEXT })

    const url = '/myaccount/transfer/send/set/dc-context'
    post(url)
      .then(() => {
        logger.info('set Dualcontrol context')
        if (handleNext) {
          handleNext()
        }
      })
      .catch(err => {
        Actions.handleError(dispatch)(err)
      })
  }
}

export const getDCSendMoneyReqPayload = state => {
  const {
    recipient: { payerId = '', recipient, displayName = '', fullName = '' },
    transaction: { isPurchase },
    unilateral: { countryCode, name: recipientFullName } = {},
    amount: { amount, currencyCode },
    note: { note: description },
    address: {
      selectedShippingAddress: { id: selectedShippingAddressId } = {},
    },
  } = state

  const recipientId = payerId || recipient
  let recipientType

  if (isEmail(recipientId)) {
    recipientType = 'EMAIL'
  } else if (isPhone(recipientId)) {
    recipientType = 'PHONE'
  } else {
    recipientType = 'ACCOUNT_NUMBER'
  }

  const payload = {
    payee: {
      id: recipientId,
      type: recipientType,
      name: displayName || fullName || recipientFullName,
      country_code: countryCode,
      is_paypal_account: !!payerId,
    },
    options: [
      {
        payment_amount: {
          currency: currencyCode,
          value: amount,
        },
      },
    ],
    payment_type: isPurchase ? PAYMENT_TYPE.PURCHASE : PAYMENT_TYPE.PERSONAL,
  }
  return { payload, description, selectedShippingAddressId }
}

export function handleSendMoneyRequest() {
  return (dispatch, getState) => {
    dispatch({ type: types.TRANSACTION.SEND_MONEY })
    const data = getDCSendMoneyReqPayload(getState())

    const url = '/myaccount/transfer/send/approval-requests'
    post(url, data)
      .then(response => {
        dispatch(sentMoney(response))
        Actions.resetScrollPosition()
      })
      .catch(err => {
        Actions.handleError(dispatch)(err)
      })
  }
}

export function handleSendSubmit() {
  return (dispatch, getState) => {
    const {
      server: { env, country },
      external: {
        donationContext,
        isRecurring,
        donationGiftAidCheckbox,
        recipientIdType,
        recipientId, // paypalme slug
      },
      amount: { amount, currencyCode },
      recipient: { recipient },
      transaction: {
        isPurchase,
        whoPaysFee,
        selectedFundingOption,
        selectedFundingOption: { id: fundingOptionId },
        selectedInstallmentPlan: { installmentTerm },
      },
      note: { note: description },
      user,
    } = getState()

    const dflFmxId = () => {
      const selectedFOExists = !!Object.keys(selectedFundingOption).length
      return (
        selectedFOExists &&
        selectedFundingOption.funding_sources.map(source => source.id)
      )
    }

    const usingPreferred = () => {
      const selectedFOExists = !!Object.keys(selectedFundingOption).length
      const answer =
        selectedFOExists &&
        selectedFundingOption.funding_sources.some(source => source.isPreferred)
      return answer ? 'Y' : 'N'
    }

    let fltp = ''
    switch (getFlowType()) {
      case 'buy':
        fltp = 'g&s'
        break
      case 'send':
        fltp = 'f&f'
        break
      default:
        break
    }

    const data = {
      fltp: fltp,
      dfl_fmxid: dflFmxId() || 'nodefault', // selected instruments
      is_pref: usingPreferred(),
      slug_name: recipientIdType === 'ppme' && recipientId,
      pp_flow: env,
    }

    const linkName = 'Send'

    const pageGroup = `main:p2p:unified:${getFlowType()}::review`
    const pageName = `main:p2p:unified:${getFlowType()}::review:node::`

    dispatch({
      type: '', // empty call to hit the analytics middleware
      meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName,
        pageGroup: pageGroup,
        pageName: pageName,
        data: data,
        trackType: 'link',
      }),
    })

    // For v1, installmentTerm is a Number, but should be string for ajax call to server
    // For v2, installmentTerm is in Iso8601 format as Service only accepts duration in that format
    const installment_term = installmentTerm && installmentTerm.toString()
    const action = isPurchase ? 'buy' : 'send'

    const sendData = {
      env,
      action,
      recipient,
      amount,
      whoPaysFee,
      currencyCode,
      fundingOptionId,
      description,
      installment_term,
      donationGiftAidCheckbox,
      flowType: getFlowType(),
      travelRuleRequirements: calculateAttributesToCollect(
        selectedFundingOption
      ),

      // external variables
      ...(env === 'donate' && { donationContext }),
    }

    if (env === 'donate' && isRecurring) {
      return
    }

    if (!travelRuleUtils.isTravelRule(selectedFundingOption)) {
      return dispatch(send(sendData))
    }

    if (
      travelRuleUtils.hasAllNeededTravelRuleInfo({
        ...user,
        selectedFundingOption,
      })
    ) {
      const travelRuleData = travelRuleUtils.massageTravelRuleDataForPost({
        ...user,
        selectedFundingOption,
        country,
      })

      return dispatch(postTravelRuleAndSend({ ...sendData, ...travelRuleData }))
    }

    // if travel rule but not all info is present, just return out
    return
  }
}

export function send(data) {
  // prefetch chunk file
  SendSuccess.preload()

  return (dispatch, getState) => {
    const {
      transaction: { fundingOptions, selectedFundingOption },
    } = getState()

    // we see a few transactions per day where we POST to /send with an empty string
    // for fundingOptionId, so in this case, we are passing up extra client-side
    // data, and we're checking for empty fundingOptionId on server and logging
    // this stuff to CAL
    if (!data.fundingOptionId) {
      Object.assign(data, {
        actions: getRecentActions(), // already stringified
        fundingOptions: JSON.stringify(fundingOptions),
        selectedFundingOption: JSON.stringify(selectedFundingOption),
      })
    }

    dispatch({ type: types.TRANSACTION.SEND_MONEY })
    const url = '/myaccount/transfer/send'
    post(url, data)
      .then(response => {
        dispatch(sentMoney(response))
        Actions.resetScrollPosition()
      })
      .catch(err => {
        if (
          err.responseJSON &&
          err.responseJSON.code === 'poolsCurrencyError'
        ) {
          dispatch(Actions.triggerExternalHook('noCurrencyBalanceError', err))
        }

        if (err.code === CLIENT_MISSING_REQUIRED_NOTE_FIELD) {
          return dispatch(Actions.mandatoryNoteMissing(true))
        }
        Actions.handleError(dispatch)(err)
      })
  }
}

/**
 * Post new travel rule data and send money
 * @param {}
 * @return {}
 */
export function postTravelRuleAndSend(data) {
  return dispatch => {
    dispatch({ type: types.TRANSACTION.POST_TRAVEL_RULE_DATA })
    dispatch({ type: types.TRANSACTION.SEND_MONEY })
    const url = '/myaccount/transfer/fi/travelRule'
    return post(url, data)
      .then(response => {
        dispatch({ type: types.TRANSACTION.POSTED_TRAVEL_RULE_DATA })
        dispatch(sentMoney(response))
      })
      .catch(err => {
        Actions.handleError(dispatch)(err)
        Actions.resetScrollPosition()
      })
  }
}

// mostly used for analytics right?
// TODO: any reason for using the thunk here?
export function sentMoney(response) {
  return (dispatch, getState) => {
    const {
      ppmeClaimUrl,
      showPPMeIframe,
      data: {
        transactions = {},
        compliance_info = {},
        amountFormatted,
        xsell = {},
      },
    } = response
    const {
      server: { env, paymentTypes },
      external: {
        showSuccessPage,
        recipientIdType,
        recipientId, // paypalme slug
      },
      theme: { themeId = '' },
      note: { note },
      recipient: { moneyPageOverPanel },
      transaction: {
        selectedFundingOption = {},
        is3DS2ChallengeRequired = false,
      },
    } = getState()

    let overpanelShownToUser = MONEY_PAGE.CONTROL_WEB

    // new variants only support gs/ff
    if (paymentTypes.includes('PERSONAL')) {
      overpanelShownToUser = moneyPageOverPanel
    }

    let {
      regulatory_information: {
        remittance_transfer_rule: { action } = {},
        status,
      } = {},
      payment_id,
      type = '',
    } = transactions
    let isRTR = action && action === 'DISPLAY_TRANSACTION_DISCLOSURES'
    let isOFAC = status === 'UNDER_REVIEW'
    let flavor = isRTR ? 'rtr' : ''
    flavor += isOFAC ? 'ofac' : ''
    const flowType = getFlowType()

    let pageGroup = `main:p2p:unified:${flowType}::done`
    let pageName = `main:p2p:unified:${flowType}::done:node::${flavor}`

    let pp_flow = themeId && flowType === 'send' ? 'p2p_gift' : undefined
    if (!pp_flow) {
      pp_flow = env
    }

    let has_note = note.length ? 1 : 0

    const threeDS2Contingency = extract3DS2Contingency(selectedFundingOption)
    const has3DS2Contingency = !isEmpty(threeDS2Contingency)
    const threeDS2ReferenceId = has3DS2Contingency
      ? threeDS2Contingency.three_ds_reference_id
      : ''

    let data = {
      trid: payment_id,
      txn_amt: amountFormatted,
      type,
      pp_flow,
      slug_name: recipientIdType === 'ppme' && recipientId,
      fltp: undefined,
      has_note,
      is_3ds_eligible: has3DS2Contingency,
      '3ds_ref_id': threeDS2ReferenceId,
      challenge_shown: is3DS2ChallengeRequired,
      page_variant: overpanelShownToUser,
    }

    dispatch({
      type: types.TRANSACTION.SENT_MONEY,
      meta: Actions.getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName: flowType || 'sentMoneySuccessful',
        pageGroup,
        pageName,
        data,
      }),
      data: {
        paymentDetails: transactions, // this is not currently used, keeping just in case it's relevant
        complianceInfo: compliance_info,
        amountFormatted,
        ppmeClaimUrl,
        showPPMeIframe,
      },
      receivedAt: Date.now(),
    })

    if (!isEmpty(xsell)) {
      dispatch({
        type: types.XSELL.DONE_XSELL_CONTENT,
        data: {
          xsell,
        },
      })
    }

    // we're also going to re-direct here after dispatching that message
    const targetUrl = `/homepage/${getFlowType()}/success`

    if (showSuccessPage) {
      routeTo(targetUrl, { replace: true })
    }

    dispatch({ type: 'externalHook:sendSuccess' })
  }
}

function showUnilateral(userCountryCode) {
  // default recipient's country to sender's country.
  // Limit this change to US for now.
  const countryFeatures = FeaturesConfig[userCountryCode] || {}
  if (countryFeatures.defaultUnilateralRecipientCountry) {
    return {
      type: types.TRANSACTION.SHOW_UNILATERAL,
      data: { userCountryCode },
    }
  }

  return {
    type: types.TRANSACTION.SHOW_UNILATERAL,
    data: { userCountryCode: '' },
  }
}

export function setPreferredFundingSource(fundingSourceId) {
  return dispatch => {
    const matchBalanceSourceIds = /^HO/ // each balance has it's own id, so we've been given api to pass just BALANCE to cover everything
    fundingSourceId = matchBalanceSourceIds.test(fundingSourceId)
      ? 'BALANCE'
      : fundingSourceId
    dispatch({ type: types.TRANSACTION.POST_PREFERRED_FUNDING_SOURCE })
    return post('/myaccount/transfer/setPreferredFundingSource', {
      fundingSourceId,
    })
      .then(response => {
        dispatch({ type: types.TRANSACTION.POSTED_PREFERRED_FUNDING_SOURCE })
        dispatch(updatePreferredInClientData(fundingSourceId))
      })
      .catch(Actions.handleError(dispatch))
  }
}

/**
 * An action which will take a target source id which has just been set preferred at service layer, and will manually
 * add the preferred tag to the funding options in client side data. We must also remove preferred tag from anything else
 * that was preferred. This is so that we don't have to fetch again when the user saves a source as preferred.
 */
function updatePreferredInClientData(targetSourceId) {
  return (dispatch, getState) => {
    const {
      transaction: { fundingOptions, selectedFundingOption },
    } = getState()
    const optionsWithPreferredRemoved = fundingOptions.map(
      removePreferredFromSourcesInOption
    )
    const optionsWithPreferredAdded = optionsWithPreferredRemoved.map(option =>
      addPreferredToSourceInOption(option, targetSourceId)
    )
    const selectedOptionWithPreferredRemoved = removePreferredFromSourcesInOption(
      selectedFundingOption
    )
    const selectedOptionWithPreferredAdded = addPreferredToSourceInOption(
      selectedOptionWithPreferredRemoved,
      targetSourceId
    )
    dispatch({
      type: types.TRANSACTION.UPDATE_PREFERRED_IN_CLIENT_DATA,
      data: {
        fundingOptions: optionsWithPreferredAdded,
        selectedFundingOption: selectedOptionWithPreferredAdded,
      },
    })
  }
}

/**
 * Resolves the Open Banking RTB (Real Time Balance) REFRESH and CONSENT contingencies
 * @param {string} action - Contingency to resolve
 * @param {Function} handleNext - Callback to execute after REFRESH contingency is resolved
 * @param {string} fundingId - This is only used when action is CONTINGENCY.CONSENT as it is not available in the store
 * @returns {Function}
 */
export function resolveRTBContingency(
  action,
  handleNext,
  fundingOptionId,
  base64EncodedJWTErrorFromWallet,
  trackFIContingency = () => {}
) {
  const url = `/myaccount/transfer/send/rtb-contingency`
  const payload = {
    fundingOptionId,
    action,
    // CONTINGENCY.INVALID_CREDENTIALS is a wrapper around CONTINGENCY.REFRESH
    // used internally in p2pnodeweb to activate the CONSENT flow.
    ...(action === CONTINGENCY.INVALID_CREDENTIALS
      ? { action: CONTINGENCY.REFRESH }
      : {}),
    base64EncodedJWTErrorFromWallet,
  }

  return async (dispatch, getState) => {
    const {
      transaction: { fundingOptions },
      server: { env },
    } = getState()
    dispatch({ type: types.TRANSACTION.RESOLVE_RTB_CONTINGENCY })
    trackFIContingency()
    await fetch(url, payload)
      .then(response => {
        if (response && response.isRTBSuccess) {
          if (isV2PhaseTwo(env) && response.reviewRequired) {
            const bankWithEcheck =
              fundingOptions.find(({ id }) => id === fundingOptionId) || {}
            trackFIContingency(response.reviewRequired ? 'echeck' : 'success')
            batch(() => {
              dispatch(
                fetchFundingOptions({
                  preselectedFundingOption: bankWithEcheck,
                  handleFISelectorNext: handleNext,
                })
              )
              dispatch(Actions.changeSelectedFundingOption(fundingOptionId))
            })
          } else {
            trackFIContingency(response.eCheck ? 'echeck' : 'success')
            batch(() => {
              dispatch({
                type: types.TRANSACTION.RESOLVED_RTB_CONTINGENCY,
                data: {
                  fundingOptionId,
                  action,
                  eCheck: response.eCheck,
                },
              })
              dispatch(Actions.changeSelectedFundingOption(fundingOptionId))
            })
            if (handleNext) {
              handleNext()
            }
          }
        }
      })
      .catch(err => {
        const response = err.responseJSON
        if (response && !response.isRTBSuccess && response.bankLoginRequired) {
          // Open Banking REFRESH_REAL_TIME_BALANCE contingency failed with invalid credentials
          // We change the contingency from REFRESH to CONSENT

          trackFIContingency('invalid_credentials')
          dispatch(Actions.changeSelectedFundingOption(fundingOptionId))
          dispatch({
            type: types.TRANSACTION.RTB_INVALID_CREDENTIALS,
            data: {
              ...payload,
              newContingency: CONTINGENCY.INVALID_CREDENTIALS,
            },
          })
          if (handleNext) {
            handleNext()
          }
          return
        }
        trackFIContingency('failure')
        if (err.code === 'errorPaymentDeniedForBank') {
          const fundingOption =
            fundingOptions.find(({ id }) => id === fundingOptionId) || {}
          const fundingSource = getRelevantFundingSource(fundingOption) || {}
          const accountType = buildFundingSourceAccountType(fundingSource)

          batch(() => {
            dispatch({
              type: types.TRANSACTION.RTB_ERROR,
              data: {
                fundingOptionId,
              },
            })

            dispatch(toggleFIUnavailableWarning(true, accountType))
          })
        } else {
          // 500s and unhandled exceptions
          dispatch(Actions.showError(err))
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
  }
}

export function saveSuccessfullyAddedFundingInstrumentIds(
  successfullyAddedFundingInstrumentIds
) {
  return {
    type: types.TRANSACTION.SAVE_SUCCESS_ID,
    data: { successfullyAddedFundingInstrumentIds },
  }
}

export function setOpenBankingError(openBankingError) {
  return {
    type: types.TRANSACTION.SET_OPEN_BANKING_ERROR,
    data: { openBankingError },
  }
}

export function toggleFIUnavailableWarning(unavailableFI, accountType) {
  return {
    type: types.TRANSACTION.UNAVAILABLE_FI,
    data: { unavailableFI, accountType },
  }
}

export function redirectToBankLogin(data) {
  const mainWindow = isIframe() ? window.top : window
  const url = '/myaccount/transfer/flow/fi/banklogin'
  return dispatch => {
    return postJson(url, JSON.stringify(data))
      .then(({ redirectUrl }) => {
        mainWindow.location = redirectUrl
      })
      .catch(err => {
        Actions.handleError(dispatch)(err)
        Actions.resetScrollPosition()
      })
  }
}
